// Where did a website guest come from?
// On every full page load of the public booking sites we classify the visit
// (ChatGPT ad, Google Ads, Google organic, ChatGPT organic, referral, direct)
// and keep the first and the latest non-direct touch in localStorage for 30 days.
// The booking form sends this summary with the reservation so it is saved on the booking.

const STORAGE_KEY = 'diamAttribution';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const PUBLIC_PATH = /^\/(airport-booking|rothschild-booking)(\/|$)/i;
const SEARCH_HOSTS = /(^|\.)(google|bing|duckduckgo|yahoo|yandex|ecosia)\./i;
const AI_HOSTS = /(^|\.)(chatgpt\.com|openai\.com|perplexity\.ai|gemini\.google\.com|copilot\.microsoft\.com|claude\.ai)$/i;
const OWN_HOST = /(^|\.)diamhotels\.com$/i;

const clip = (v, n = 80) => (typeof v === 'string' ? v.trim().slice(0, n) : '');

function hostOf(url) {
  try { return new URL(url).hostname.toLowerCase(); } catch (_) { return ''; }
}

// Pure classification of one visit. Returns null for internal navigation.
function classifyVisit(href, referrer) {
  let url;
  try { url = new URL(href); } catch (_) { return null; }
  const q = url.searchParams;
  const refHost = hostOf(referrer);
  const touch = { source: '', medium: '', campaign: clip(q.get('utm_campaign')), referrer: OWN_HOST.test(refHost) ? '' : refHost, landing: clip(url.pathname, 120) };

  if (q.get('utm_source')) {
    touch.source = clip(q.get('utm_source'));
    touch.medium = clip(q.get('utm_medium')) || 'unknown';
  } else if (q.get('oppref')) {
    touch.source = 'chatgpt'; touch.medium = 'cpc';
  } else if (q.get('gclid') || q.get('gbraid') || q.get('wbraid')) {
    touch.source = 'google'; touch.medium = 'cpc';
  } else if (q.get('fbclid')) {
    touch.source = 'facebook'; touch.medium = 'social';
  } else if (refHost && !OWN_HOST.test(refHost)) {
    if (AI_HOSTS.test(refHost)) { touch.source = refHost.replace(/^www\./, ''); touch.medium = 'ai'; }
    else if (SEARCH_HOSTS.test(refHost)) { touch.source = refHost.replace(/^www\./, '').split('.')[0]; touch.medium = 'organic'; }
    else { touch.source = refHost.replace(/^www\./, ''); touch.medium = 'referral'; }
  } else if (refHost && OWN_HOST.test(refHost)) {
    return null; // moving inside our own site
  } else {
    touch.source = 'direct'; touch.medium = 'none';
  }
  return touch;
}

function read(storage, now) {
  try {
    const data = JSON.parse(storage.getItem(STORAGE_KEY) || 'null');
    if (!data || typeof data !== 'object') return null;
    if (data.last && now - Number(data.last.at || 0) > TTL_MS) return null;
    return data;
  } catch (_) { return null; }
}

// Called once per full page load.
function captureAttribution(href, referrer, storage, now = Date.now()) {
  try {
    if (!storage) return null;
    const path = (() => { try { return new URL(href).pathname; } catch (_) { return ''; } })();
    if (!PUBLIC_PATH.test(path)) return read(storage, now);
    const touch = classifyVisit(href, referrer);
    const data = read(storage, now) || {};
    if (touch) {
      const stamped = { ...touch, at: now };
      if (!data.first) data.first = stamped;
      // A direct visit never overwrites a known marketing source.
      if (touch.source !== 'direct' || !data.last) data.last = stamped;
      storage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
    return data;
  } catch (_) { return null; }
}

// Summary sent with the booking (no personal data).
function attributionForBooking(storage, now = Date.now()) {
  const data = storage ? read(storage, now) : null;
  if (!data) return undefined;
  const pick = t => t && ({ source: t.source, medium: t.medium, campaign: t.campaign, referrer: t.referrer, landing: t.landing, at: new Date(t.at).toISOString() });
  return { first: pick(data.first), last: pick(data.last) };
}

// Short Hebrew label for the admin screens, e.g. "ChatGPT – מודעה (airport)".
function describeTouch(t) {
  if (!t || !t.source) return '';
  const src = String(t.source).toLowerCase();
  const med = String(t.medium || '').toLowerCase();
  let label;
  if (src.startsWith('chatgpt') && (med === 'cpc' || med === 'paid_ai')) label = 'ChatGPT – מודעה';
  else if (src.startsWith('chatgpt') || med === 'ai') label = `${t.source} – המלצה חינמית`;
  else if (src === 'google' && med === 'cpc') label = 'גוגל – מודעה';
  else if (med === 'organic') label = `${t.source} – חיפוש רגיל`;
  else if (src === 'direct') label = 'כניסה ישירה';
  else label = `${t.source}${med && med !== 'referral' ? ` (${med})` : ''}`;
  return t.campaign ? `${label} · ${t.campaign}` : label;
}

function describeAttribution(a) {
  if (!a || (!a.first && !a.last)) return '';
  const last = describeTouch(a.last);
  const first = describeTouch(a.first);
  if (!first || first === last) return last || first;
  return `${last} (ביקור ראשון: ${first})`;
}

module.exports = { STORAGE_KEY, classifyVisit, captureAttribution, attributionForBooking, describeAttribution };
